import { useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../../utils/errors';
import {
  computeChangedFields,
  displayBaselineValue,
  isFieldChanged,
  rowAsBaseline,
  type EditableField,
} from '@/lib/corrections';
import type { RaceLaneResult } from '../../types';
import { Alert, Button, Input, Label, Spinner, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, cn } from '@racing/ui';

interface RaceDataEditDialogProps {
  open: boolean;
  record: RaceLaneResult | null;
  originalRecord: Record<string, any> | null;
  onClose: () => void;
  onSave: (id: number, changedFields: Record<string, any>, reason?: string) => Promise<void>;
}

/** The correctable fields, grouped the way the form reads them. */
const GROUPS: Array<{ title: string; fields: EditableField[] }> = [
  {
    title: 'Driver and vehicle',
    fields: [
      { key: 'carNo', label: 'Car No' },
      { key: 'driver', label: 'Driver' },
      { key: 'carClass', label: 'Car Class' },
    ],
  },
  {
    title: 'Race performance',
    fields: [
      { key: 'rt', label: 'RT' },
      { key: 'ft60', label: '60 ft' },
      { key: 'ft330', label: '330 ft' },
      { key: 'ft8', label: '1/8 mi' },
      { key: 'ft8Mph', label: '1/8 MPH' },
      { key: 'ft1000', label: '1000 ft' },
      { key: 'ft4', label: '1/4 mi' },
      { key: 'ft4Mph', label: '1/4 MPH' },
      { key: 'dialIn', label: 'Dial-In' },
      { key: 'mov', label: 'MOV' },
    ],
  },
  {
    title: 'Result',
    fields: [
      { key: 'win', label: 'Win' },
      { key: 'first', label: 'First' },
      { key: 'bumpSpot', label: 'Bump Spot' },
      { key: 'qPos', label: 'QPos' },
    ],
  },
  {
    title: 'Timing strings',
    fields: [
      { key: 'rtStr', label: 'RT Str' },
      { key: 'ft60Str', label: '60 ft Str' },
      { key: 'ft330Str', label: '330 ft Str' },
      { key: 'ft8Str', label: '1/8 mi Str' },
      { key: 'ft8MphStr', label: '1/8 MPH Str' },
      { key: 'ft1000Str', label: '1000 ft Str' },
      { key: 'ft4Str', label: '1/4 mi Str' },
      { key: 'ft4MphStr', label: '1/4 MPH Str' },
      { key: 'dialInStr', label: 'Dial-In Str' },
      { key: 'firstStr', label: 'First Str' },
      { key: 'qPosStr', label: 'QPos Str' },
    ],
  },
];

/** Shown for context but never correctable. */
const READ_ONLY: Array<{ key: keyof RaceLaneResult; label: string }> = [
  { key: 'lane', label: 'Lane' },
  { key: 'eventName', label: 'Event' },
  { key: 'category', label: 'Category' },
  { key: 'round', label: 'Round' },
  { key: 'raceId', label: 'Race ID' },
];

const RaceDataEditDialog = ({
  open,
  record,
  originalRecord,
  onClose,
  onSave,
}: RaceDataEditDialogProps) => {
  const [fields, setFields] = useState<Partial<RaceLaneResult>>({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (record) {
      setFields({ ...record });
      setReason('');
      setError('');
    }
  }, [record]);

  /**
   * What edits are measured against.
   *
   * Prefer the server's original record. When that fetch failed, fall back to a
   * projection of the row itself — previously the comparison ran against
   * `null`, which made every field look unchanged, so the user could type a
   * correction and only ever be told "No fields have been changed".
   */
  const baseline = useMemo(
    () => originalRecord ?? rowAsBaseline(record),
    [originalRecord, record]
  );

  const changedCount = useMemo(
    () => Object.keys(computeChangedFields(fields, baseline)).length,
    [fields, baseline]
  );

  const handleSave = async () => {
    if (!record) return;
    try {
      setSaving(true);
      setError('');

      const changedFields = computeChangedFields(fields, baseline);
      if (Object.keys(changedFields).length === 0) {
        setError('No fields have been changed');
        return;
      }

      await onSave(record.raceLaneResultId, changedFields, reason || undefined);
      onClose();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  const renderField = ({ key, label }: EditableField) => {
    const changed = isFieldChanged(fields, baseline, key as string);
    const wasValue = originalRecord ? displayBaselineValue(originalRecord, key as string) : undefined;
    const id = `correction-${String(key)}`;
    return (
      <div key={String(key)} className="flex flex-col gap-1.5">
        <Label htmlFor={id} className={cn(changed && 'text-brand-deep')}>
          {label}
        </Label>
        <Input
          id={id}
          value={(fields[key] as string | number | null) ?? ''}
          // Stored raw. Parsing here would make decimals impossible to type:
          // Number('0.') is 0, so the decimal point vanishes on the keystroke
          // that creates it. computeChangedFields parses at save instead.
          onChange={(event) =>
            setFields((prev) => ({ ...prev, [key]: event.target.value }))
          }
          className={cn(changed && 'border-brand bg-brand/5')}
        />
        {wasValue !== undefined && (
          <p className={cn('font-sans text-xs', changed ? 'text-brand-deep' : 'text-ink-faint')}>
            {changed ? `Was ${wasValue}` : `Original: ${wasValue}`}
          </p>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Correct race record</DialogTitle>
          <DialogDescription>
            Creates a versioned correction — the original data is preserved.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-6">
          {error && <Alert variant="danger">{error}</Alert>}

          {!originalRecord && (
            <Alert variant="warning">
              The original record could not be loaded, so before-and-after values are not shown.
              Edits are compared against the row as it stands.
            </Alert>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="correction-reason">Correction reason (optional)</Label>
            <Input
              id="correction-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Timing system error, wrong lane assignment"
            />
          </div>

          {GROUPS.map((group) => (
            <section key={group.title} className="flex flex-col gap-3">
              <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
                {group.title}
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                {group.fields.map(renderField)}
              </div>
            </section>
          ))}

          <section className="flex flex-col gap-3">
            <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
              Context (read only)
            </h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
              {READ_ONLY.map(({ key, label }) => (
                <div key={String(key)} className="flex flex-col gap-1.5">
                  <Label htmlFor={`context-${String(key)}`} className="text-ink-faint">
                    {label}
                  </Label>
                  <Input
                    id={`context-${String(key)}`}
                    value={(fields[key] as string | number | null) ?? ''}
                    disabled
                  />
                </div>
              ))}
            </div>
          </section>
        </DialogBody>

        <DialogFooter>
          <span className="mr-auto self-center font-sans text-sm text-ink-soft">
            {changedCount === 0
              ? 'No changes yet'
              : `${changedCount} field${changedCount === 1 ? '' : 's'} changed`}
          </span>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || changedCount === 0}>
            {saving && <Spinner className="size-4 text-white" label={null} />}
            {saving ? 'Saving…' : 'Save correction'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RaceDataEditDialog;
